/**
 * Normalisation des réglages de sondage avant persistance.
 *
 * Trois clés ont longtemps doublé un réglage que le serveur applique déjà. Le
 * doublon n'est pas cosmétique : c'est le mécanisme par lequel un contrôle
 * cesse d'exister sans que rien ne casse — l'UI écrivait la clé morte, le SQL
 * lisait l'autre, et personne ne voyait l'écart.
 *
 * | Clé retirée               | Ce qui tient la garantie                          |
 * | ------------------------- | ------------------------------------------------- |
 * | `maxParticipants`         | `maxResponses`, appliqué dans les deux RPC        |
 * | `allowAnonymousVotes`     | `requireAuth`, appliqué dans les deux RPC         |
 * | `allowAnonymousResponses` | idem                                              |
 * | `oneResponsePerPerson`    | contrainte d'unicité (poll_id, stable_fingerprint) |
 * | `emailForCopy`            | `resultsCopyEmail`, lu par enqueue_vote_notification |
 *
 * La migration `20260816093000` les a effacées des sondages existants ; sans
 * ce garde-fou, le front les réécrivait à chaque création.
 *
 * ⚠️ Ne concerne QUE les réglages de sondage. `allowAnonymousVotes` existe
 * aussi comme propriété d'une question de type date à l'intérieur d'un
 * questionnaire (`DateQuestion`) — concept différent, laissé intact : cette
 * fonction ne touche jamais aux questions.
 */

/** Clés de réglage retirées, listées pour les tests et la traçabilité. */
export const LEGACY_SETTING_KEYS = [
  "emailForCopy",
  "maxParticipants",
  "oneResponsePerPerson",
  "allowAnonymousVotes",
  "allowAnonymousResponses",
] as const;

export type LegacySettingKey = (typeof LEGACY_SETTING_KEYS)[number];

/**
 * Retire les clés mortes d'un objet de réglages, en reportant d'abord ce
 * qu'elles portaient de signifiant sur la clé réellement appliquée.
 *
 * - `allowAnonymous* === false` signifie « il faut être connecté » : c'est
 *   exactement `requireAuth`. Reporté seulement si `requireAuth` n'est pas déjà
 *   décidé, pour ne jamais contredire un choix explicite.
 * - `maxParticipants` n'est **pas** reporté sur `maxResponses` : aucune UI ne
 *   l'a jamais exposé, sa seule origine était une valeur par défaut (10) que
 *   personne n'avait choisie. La convertir en plafond appliqué côté serveur
 *   transformerait un défaut oublié en refus de vote — précisément le genre de
 *   durcissement silencieux qu'on cherche à éviter.
 * - `oneResponsePerPerson` disparaît sans report : l'unicité est structurelle.
 * - `emailForCopy` portait DEUX choses : une liste d'invités et l'adresse de
 *   copie. La migration 20260816093000 les a séparées ; on reprend ici sa
 *   règle pour la partie copie (première adresse, seulement si la case est
 *   cochée). Le chemin de création passe déjà par `stripEmailFields`, mais
 *   pas celui d'édition — d'où ce report ici, au point commun aux deux.
 */
export function normalizePollSettings<T extends Record<string, any>>(
  settings: T | null | undefined,
): T | null | undefined {
  if (!settings || typeof settings !== "object") return settings;

  const out: Record<string, any> = { ...settings };

  // `emailForCopy` → `resultsCopyEmail` : même rôle, mais seul le second est lu
  // par le serveur. Le report n'a de sens que si la copie est demandée.
  if (
    out.resultsCopyEmail === undefined &&
    out.sendEmailCopy === true &&
    typeof out.emailForCopy === "string"
  ) {
    // `btrim` AVANT le découpage : sinon un espace de tête produit un premier
    // segment vide et l'adresse est perdue en silence.
    const first = out.emailForCopy
      .trim()
      .split(/[,;\s]+/)[0]
      ?.trim()
      .toLowerCase();
    if (first) out.resultsCopyEmail = first;
  }

  const anonymousDenied =
    out.allowAnonymousVotes === false || out.allowAnonymousResponses === false;
  if (anonymousDenied && out.requireAuth === undefined) {
    out.requireAuth = true;
  }

  for (const key of LEGACY_SETTING_KEYS) {
    delete out[key];
  }

  return out as T;
}

/**
 * Lecture tolérante, pour les données produites avant cette normalisation
 * (brouillons `localStorage`, sondages jamais réenregistrés) : un sondage qui
 * porte encore `allowAnonymous*: false` doit continuer à exiger un compte.
 */
export function resolveRequireAuth(
  settings: Record<string, any> | null | undefined,
): boolean {
  if (!settings) return false;
  if (settings.requireAuth === true || settings.requireAuth === "true") {
    return true;
  }
  return (
    settings.allowAnonymousVotes === false ||
    settings.allowAnonymousResponses === false
  );
}
